import React, { useEffect, useMemo, useState } from "react";
import { api, fmtEUR, fmtNum, API_BASE } from "../lib/api";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend, PieChart, Pie, Cell } from "recharts";
import { Coins, Car, FileText, Wallet, Scale, TrendingDown, AlertTriangle, PiggyBank } from "lucide-react";

const MONTHS = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];

/* ===================== UI PRIMITIVES — Luxury Black & Gold ===================== */

function Card({ children, className = "", testid }) {
  return (
    <div data-testid={testid} className={`rounded-2xl border border-amber-500/15 bg-gradient-to-b from-[#141a26] to-[#0b1018] shadow-[0_0_0_1px_rgba(245,203,92,0.05)] ${className}`}>
      {children}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, tone = "gold", testid }) {
  const tones = {
    gold:    { border: "border-amber-500/30",  bg: "from-amber-500/10 to-transparent", text: "text-amber-300",  iconBg: "bg-amber-500/15 text-amber-300" },
    purple:  { border: "border-violet-500/30", bg: "from-violet-500/10 to-transparent", text: "text-violet-300", iconBg: "bg-violet-500/15 text-violet-300" },
    sky:     { border: "border-sky-500/30",    bg: "from-sky-500/10 to-transparent",    text: "text-sky-300",    iconBg: "bg-sky-500/15 text-sky-300" },
    emerald: { border: "border-emerald-500/30",bg: "from-emerald-500/10 to-transparent",text: "text-emerald-300",iconBg: "bg-emerald-500/15 text-emerald-300" },
    rose:    { border: "border-rose-500/30",   bg: "from-rose-500/10 to-transparent",   text: "text-rose-300",   iconBg: "bg-rose-500/15 text-rose-300" },
    amberWarn:{border: "border-amber-400/30",  bg: "from-amber-400/10 to-transparent",  text: "text-amber-200",  iconBg: "bg-amber-400/15 text-amber-200" },
  };
  const t = tones[tone] || tones.gold;
  return (
    <div
      data-testid={testid}
      className={`relative overflow-hidden rounded-2xl border ${t.border} bg-gradient-to-b ${t.bg} p-4 sm:p-5`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[9px] uppercase tracking-[0.18em] text-slate-400 font-mono-num font-bold">{label}</div>
          <div className={`font-mono-num font-extrabold ${t.text} mt-1.5 text-xl sm:text-2xl lg:text-[26px] leading-tight whitespace-nowrap`}>{value}</div>
          {sub && <div className="text-[10px] text-slate-500 font-mono-num mt-1">{sub}</div>}
        </div>
        {Icon && (
          <div className={`shrink-0 rounded-xl ${t.iconBg} w-9 h-9 flex items-center justify-center`}>
            <Icon size={16}/>
          </div>
        )}
      </div>
      <div className={`absolute -right-6 -bottom-6 w-24 h-24 rounded-full ${t.iconBg} opacity-20 blur-2xl`}/>
    </div>
  );
}

function SectionTitle({ children, right }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <h3 className="font-display text-sm sm:text-base font-extrabold text-amber-300 uppercase tracking-[0.18em]">{children}</h3>
      {right}
    </div>
  );
}

/* ===================== HELPERS ===================== */

const COLORS = {
  gold: "#F5CB5C",
  gold2: "#C9A227",
  purple: "#8B5CF6",
  purpleLite: "#C4B5FD",
  sky: "#38BDF8",
  skyLite: "#7DD3FC",
  emerald: "#10B981",
  rose: "#F43F5E",
  slate: "#64748b",
};

function fmtDay(d) {
  if (!d) return "-";
  const [, m, dd] = d.split("-");
  return `${dd}/${m}`;
}

/* ===================== MAIN COMPONENT ===================== */

export default function ReportMensile() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/reports/full-monthly", { params: { year, month } })
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [year, month]);

  const d = data;
  const pdfHref = `${API_BASE}/reports/full-monthly.pdf?year=${year}&month=${month}`;

  // ----- Charts data -----
  const chartDaily = useMemo(() => {
    if (!d) return [];
    return d.daily.map(x => ({
      day: fmtDay(x.date),
      NCC: x.ncc_total,
      Uber: x.uber_total,
      Cash: x.cash,
      Totale: x.total,
    }));
  }, [d]);

  const pieCategory = useMemo(() => {
    if (!d) return [];
    return [
      { name: "Uber", value: d.uber.total, color: COLORS.purple },
      { name: "NCC",  value: d.ncc.total,  color: COLORS.sky },
    ];
  }, [d]);

  const piePayment = useMemo(() => {
    if (!d) return [];
    return [
      { name: "Elettronico", value: d.totals.electronic_total, color: COLORS.gold2 },
      { name: "Cash",        value: d.totals.cash_total,        color: COLORS.gold },
    ];
  }, [d]);

  return (
    <div className="space-y-5" data-testid="report-mensile-page">
      {/* ===== HEADER ===== */}
      <div className="rounded-2xl border border-amber-500/15 bg-gradient-to-br from-[#0f141f] via-[#0a0f18] to-[#07090e] p-5 sm:p-6 overflow-hidden relative">
        <div className="absolute inset-0 opacity-10 pointer-events-none bg-[radial-gradient(ellipse_at_top_right,rgba(245,203,92,0.4),transparent_50%)]"/>
        <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-amber-400/90 font-mono-num font-bold">Striano NCC</div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mt-1 bg-gradient-to-br from-amber-200 via-amber-400 to-amber-600 bg-clip-text text-transparent">Report Mensile</h1>
            <div className="text-sm text-slate-400 mt-1">{d?.label || `${MONTHS[month-1]} ${year}`}</div>
          </div>
          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Mese</Label>
              <Select value={String(month)} onValueChange={v=>setMonth(Number(v))}>
                <SelectTrigger data-testid="report-month-select" className="bg-slate-950 border-amber-500/20 w-36"><SelectValue/></SelectTrigger>
                <SelectContent className="bg-slate-950 border-slate-800">
                  {MONTHS.map((m,i)=><SelectItem key={i} value={String(i+1)}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Anno</Label>
              <Input data-testid="report-year-input" type="number" value={year} onChange={e=>setYear(Number(e.target.value))} className="bg-slate-950 border-amber-500/20 w-24"/>
            </div>
            <a data-testid="report-pdf-btn" href={pdfHref} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs uppercase tracking-widest font-mono-num shadow-[0_4px_12px_rgba(245,203,92,0.3)]">
              <FileText size={14}/> PDF
            </a>
          </div>
        </div>
      </div>

      {loading && !d && <div className="text-slate-500 py-10 text-center">Caricamento report...</div>}

      {d && (
        <>
          {/* ===== KPI PRIMARI ===== */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <KpiCard testid="kpi-fatturato" icon={PiggyBank}      label="Fatturato Totale" value={fmtEUR(d.totals.revenue_total)} sub={`${d.totals.services_total} servizi`} tone="gold"/>
            <KpiCard testid="kpi-uber"      icon={Coins}          label="Uber Totale"      value={fmtEUR(d.uber.total)}            sub={`${d.uber.count} aggregati`}         tone="purple"/>
            <KpiCard testid="kpi-ncc"       icon={Car}            label="Servizi NCC"      value={fmtEUR(d.ncc.total)}             sub={`${d.ncc.count} servizi`}            tone="sky"/>
            <KpiCard testid="kpi-cash"      icon={Wallet}         label="Cash (totale)"    value={fmtEUR(d.totals.cash_total)}     sub="Uber Cash + NCC Cash"                tone="amberWarn"/>
          </div>

          {/* ===== KPI SECONDARI ===== */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <KpiCard testid="kpi-collected"   icon={Wallet}       label="Incassato"     value={fmtEUR(d.collected.total)} sub={`${fmtEUR(d.collected.cash)} cash · ${fmtEUR(d.collected.electronic)} elettronico`} tone="emerald"/>
            <KpiCard testid="kpi-outstanding" icon={AlertTriangle} label="Da Incassare" value={fmtEUR(d.outstanding)}      sub="Servizi non ancora saldati"                                                       tone="amberWarn"/>
            <KpiCard testid="kpi-to-pay"      icon={TrendingDown} label="Da Pagare"     value={fmtEUR(d.to_pay.total)}     sub="Autisti + Fornitori"                                                              tone="rose"/>
            <KpiCard testid="kpi-balance"     icon={Scale}        label="Saldo Mese"    value={fmtEUR(d.balance.saldo)}    sub="Da Incassare − Da Pagare"                                                         tone={d.balance.saldo >= 0 ? "emerald" : "rose"}/>
          </div>

          {/* ===== RICONCILIAZIONE ===== */}
          {!d.reconciliation.ok && (
            <div className="rounded-xl border border-amber-400/40 bg-amber-400/5 p-3 sm:p-4" data-testid="reconciliation-warning">
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-amber-300 flex-shrink-0 mt-0.5" size={18}/>
                <div className="min-w-0">
                  <div className="font-bold text-amber-200 text-sm">Attenzione: alcuni dati del report non risultano riconciliati.</div>
                  <ul className="mt-1 text-xs text-amber-200/70 space-y-0.5 list-disc list-inside">
                    {d.reconciliation.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* ===== GRAFICI ===== */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="lg:col-span-2 p-4 sm:p-5" testid="chart-andamento">
              <SectionTitle>Andamento Mensile</SectionTitle>
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <BarChart data={chartDaily} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid stroke="#1f2937" strokeDasharray="3 3" vertical={false}/>
                    <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#8a8f9c" }} interval="preserveStartEnd"/>
                    <YAxis tick={{ fontSize: 10, fill: "#8a8f9c" }}/>
                    <Tooltip contentStyle={{ background: "#0b0f17", border: "1px solid #2a2f3a", borderRadius: 8, color: "#E6E9EF", fontSize: 12 }} formatter={(v) => fmtEUR(v)}/>
                    <Legend wrapperStyle={{ fontSize: 11 }}/>
                    <Bar dataKey="NCC"  stackId="rev" fill={COLORS.sky}/>
                    <Bar dataKey="Uber" stackId="rev" fill={COLORS.purple}/>
                    <Bar dataKey="Cash" stackId="pay" fill={COLORS.gold} opacity={0.6}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="text-[10px] text-slate-500 mt-1">Il Cash è mostrato come dettaglio del metodo di pagamento (non sommato al totale giornaliero).</div>
            </Card>

            <Card className="p-4 sm:p-5" testid="chart-distribuzione">
              <SectionTitle>Distribuzione Fatturato</SectionTitle>
              <div style={{ width: "100%", height: 180 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={pieCategory} dataKey="value" nameKey="name" innerRadius={46} outerRadius={76} paddingAngle={2}>
                      {pieCategory.map((e, i) => <Cell key={i} fill={e.color}/>)}
                    </Pie>
                    <Tooltip contentStyle={{ background: "#0b0f17", border: "1px solid #2a2f3a", borderRadius: 8, fontSize: 12 }} formatter={(v) => fmtEUR(v)}/>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-1.5">
                {pieCategory.map((e, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: e.color }}/><span className="text-slate-300">{e.name}</span></div>
                    <div className="font-mono-num text-slate-200 font-bold">{fmtEUR(e.value)} <span className="text-slate-500">({d.totals.revenue_total > 0 ? Math.round((e.value / d.totals.revenue_total) * 100) : 0}%)</span></div>
                  </div>
                ))}
                <div className="border-t border-slate-800/60 pt-2 mt-2">
                  <div className="text-[9px] uppercase tracking-widest text-slate-500 font-mono-num mb-1">Metodo pagamento</div>
                  {piePayment.map((e, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-sm" style={{ background: e.color }}/><span className="text-slate-300">{e.name}</span></div>
                      <div className="font-mono-num text-slate-200 font-bold">{fmtEUR(e.value)} <span className="text-slate-500">({d.totals.revenue_total > 0 ? Math.round((e.value / d.totals.revenue_total) * 100) : 0}%)</span></div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          </div>

          {/* ===== DETTAGLI UBER / NCC / RIEPILOGO ===== */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="p-4 sm:p-5" testid="box-uber">
              <SectionTitle right={<span className="text-[10px] uppercase tracking-widest text-violet-300 font-mono-num font-bold">Uber</span>}>Uber</SectionTitle>
              <RowDetail label="Uber Elettronico"  amount={d.uber.electronic.amount} count={d.uber.electronic.count}/>
              <RowDetail label="Uber Cash"         amount={d.uber.cash.amount}       count={d.uber.cash.count}/>
              <TotalRow label="Totale Uber" amount={d.uber.total} count={d.uber.count} tone="purple"/>
              <div className="text-[10px] text-slate-500 mt-2 italic">Dati aggregati dalla sezione Uber Mensile (settimane).</div>
            </Card>

            <Card className="p-4 sm:p-5" testid="box-ncc">
              <SectionTitle right={<span className="text-[10px] uppercase tracking-widest text-sky-300 font-mono-num font-bold">NCC</span>}>Servizi NCC</SectionTitle>
              <RowDetail label="NCC Elettronico"   amount={d.ncc.electronic.amount} count={d.ncc.electronic.count}/>
              <RowDetail label="NCC Cash"          amount={d.ncc.cash.amount}       count={d.ncc.cash.count}/>
              <TotalRow label="Totale NCC" amount={d.ncc.total} count={d.ncc.count} tone="sky"/>
            </Card>

            <Card className="p-4 sm:p-5" testid="box-riepilogo">
              <SectionTitle>Totale Generale</SectionTitle>
              <RowDetail label="Totale Servizi NCC"  amount={d.ncc.total}  count={d.ncc.count}/>
              <RowDetail label="Totale Uber"          amount={d.uber.total} count={d.uber.count}/>
              <TotalRow  label="Totale Fatturato"     amount={d.totals.revenue_total} count={d.totals.services_total} tone="gold"/>
              <div className="h-px bg-slate-800/60 my-2"/>
              <RowDetail label="Cash Totale"          amount={d.totals.cash_total}        count={null} valueColor="text-amber-300"/>
              <RowDetail label="Elettronico Totale"   amount={d.totals.electronic_total}  count={null} valueColor="text-sky-300"/>
            </Card>
          </div>

          {/* ===== INCASSI / PAGAMENTI / SALDO ===== */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="p-4 sm:p-5" testid="box-incassi">
              <SectionTitle right={<span className="text-[10px] uppercase tracking-widest text-emerald-300 font-mono-num font-bold">Incassi</span>}>Incassi da Clienti</SectionTitle>
              <RowDetail label="Pagamenti elettronici (bonifico / POS / carta)" amount={d.collected.electronic} count={null}/>
              <RowDetail label="Pagamenti cash" amount={d.collected.cash} count={null}/>
              <TotalRow  label="Totale incassato" amount={d.collected.total} count={null} tone="emerald"/>
              <div className="text-[10px] text-slate-500 mt-2 italic">Dato dalla logica Incassi/Payments già esistente, nessuna modifica.</div>
            </Card>

            <Card className="p-4 sm:p-5" testid="box-pagamenti">
              <SectionTitle right={<span className="text-[10px] uppercase tracking-widest text-rose-300 font-mono-num font-bold">Pagamenti</span>}>Da Pagare</SectionTitle>
              <RowDetail label="Autisti (quote NCC)"  amount={d.to_pay.drivers_ncc}/>
              <RowDetail label="Autisti (quote Uber)" amount={d.to_pay.drivers_uber}/>
              <RowDetail label="Fornitori esterni"    amount={d.to_pay.suppliers}/>
              <TotalRow  label="Totale da pagare" amount={d.to_pay.total} count={null} tone="rose"/>
            </Card>

            <Card className="p-4 sm:p-5" testid="box-saldo">
              <SectionTitle right={<span className="text-[10px] uppercase tracking-widest text-amber-300 font-mono-num font-bold">Saldo</span>}>Saldo Finale</SectionTitle>
              <RowDetail label="Fatturato totale" amount={d.totals.revenue_total}/>
              <RowDetail label="Incassato"        amount={d.collected.total}  valueColor="text-emerald-300"/>
              <RowDetail label="Da incassare"     amount={d.outstanding}      valueColor="text-amber-300"/>
              <RowDetail label="Da pagare"        amount={d.to_pay.total}     valueColor="text-rose-300"/>
              <TotalRow  label="Saldo mese" amount={d.balance.saldo} count={null} tone={d.balance.saldo >= 0 ? "emerald" : "rose"}/>
            </Card>
          </div>

          {/* ===== DETTAGLIO GIORNALIERO ===== */}
          <Card className="p-4 sm:p-5" testid="box-daily">
            <SectionTitle right={<a data-testid="daily-pdf-link" href={pdfHref} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono-num font-bold text-amber-300 hover:text-amber-200"><FileText size={12}/> Scarica PDF</a>}>Dettaglio Giornaliero</SectionTitle>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[9px] uppercase tracking-widest text-slate-500 font-mono-num">
                  <tr className="border-b border-slate-800/60">
                    <th className="text-left p-2">Data</th>
                    <th className="text-right p-2">NCC</th>
                    <th className="text-right p-2">Uber</th>
                    <th className="text-right p-2">Cash (incl.)</th>
                    <th className="text-right p-2">Totale</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/40">
                  {d.daily.filter(x => x.total > 0).map((x) => (
                    <tr key={x.date} className="hover:bg-slate-900/30">
                      <td className="p-2 text-slate-300 font-mono-num">{fmtDay(x.date)}</td>
                      <td className="p-2 text-right font-mono-num text-sky-300">{fmtEUR(x.ncc_total)}</td>
                      <td className="p-2 text-right font-mono-num text-violet-300">{fmtEUR(x.uber_total)}</td>
                      <td className="p-2 text-right font-mono-num text-amber-300/80">{fmtEUR(x.cash)}</td>
                      <td className="p-2 text-right font-mono-num text-amber-300 font-bold">{fmtEUR(x.total)}</td>
                    </tr>
                  ))}
                  {d.daily.filter(x => x.total > 0).length === 0 && (
                    <tr><td colSpan={5} className="p-6 text-center text-slate-500">Nessun movimento nel mese selezionato</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-2">
              {d.daily.filter(x => x.total > 0).map((x) => (
                <div key={x.date} className="rounded-xl border border-slate-800/60 bg-slate-900/40 p-3">
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-mono-num text-amber-300 font-bold">{fmtDay(x.date)}</div>
                    <div className="font-mono-num text-amber-300 font-extrabold">{fmtEUR(x.total)}</div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-[10px] font-mono-num">
                    <div><div className="text-slate-500 uppercase tracking-widest">NCC</div><div className="text-sky-300">{fmtEUR(x.ncc_total)}</div></div>
                    <div><div className="text-slate-500 uppercase tracking-widest">Uber</div><div className="text-violet-300">{fmtEUR(x.uber_total)}</div></div>
                    <div><div className="text-slate-500 uppercase tracking-widest">Cash</div><div className="text-amber-300/80">{fmtEUR(x.cash)}</div></div>
                  </div>
                </div>
              ))}
              {d.daily.filter(x => x.total > 0).length === 0 && (
                <div className="p-6 text-center text-slate-500">Nessun movimento nel mese selezionato</div>
              )}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

function RowDetail({ label, amount, count, valueColor = "text-slate-200" }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-slate-800/40 last:border-0">
      <div className="text-xs text-slate-400">{label}</div>
      <div className="flex items-center gap-3 whitespace-nowrap">
        <span className={`font-mono-num font-bold ${valueColor}`}>{fmtEUR(amount)}</span>
        {count != null && <span className="text-[10px] text-slate-500 font-mono-num min-w-[70px] text-right">{count} servizi</span>}
      </div>
    </div>
  );
}

function TotalRow({ label, amount, count, tone = "gold" }) {
  const tones = {
    gold:    "border-amber-500/40 bg-amber-500/10 text-amber-300",
    purple:  "border-violet-500/40 bg-violet-500/10 text-violet-300",
    sky:     "border-sky-500/40 bg-sky-500/10 text-sky-300",
    emerald: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
    rose:    "border-rose-500/40 bg-rose-500/10 text-rose-300",
  };
  return (
    <div className={`mt-2 rounded-lg border px-3 py-2 flex items-center justify-between ${tones[tone] || tones.gold}`}>
      <div className="text-[10px] uppercase tracking-widest font-mono-num font-bold">{label}</div>
      <div className="flex items-center gap-3 whitespace-nowrap">
        <span className="font-mono-num font-extrabold">{fmtEUR(amount)}</span>
        {count != null && <span className="text-[10px] opacity-80 font-mono-num">{count} servizi</span>}
      </div>
    </div>
  );
}
